import { useEffect, useState } from "react";
import { Calculator, Settings2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EstimatorSettings } from "@/components/EstimatorSettings";
import { CATEGORIES, money } from "@/lib/format";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const hrs = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, "0")}m`;
const code = (a) => (a || "").split(" · ")[0];

function Row({ label, value, testId, strong }) {
  return (
    <div className={`flex justify-between gap-4 py-1.5 border-b border-white/5 text-sm ${strong ? "text-white" : "text-zinc-300"}`}>
      <span>{label}</span><span className="font-mono-data whitespace-nowrap" data-testid={testId}>{value}</span>
    </div>
  );
}

function Breakdown({ e }) {
  return (
    <div data-testid="estimate-breakdown">
      {e.legs.map((l, i) => (
        <Row key={i} label={`Leg ${i + 1}: ${l.from} → ${l.to} · est. ${hrs(l.est_hours)}${l.minimum_applied ? ` (${e.min_hours_per_leg}h minimum)` : ""}`} value={hrs(l.billed_hours)} testId={`estimate-leg-${i}-hours`} />
      ))}
      {e.reposition_hours > 0 && <Row label="Repositioning allowance" value={hrs(e.reposition_hours)} testId="estimate-reposition" />}
      <Row label={`Billable hours ${hrs(e.billable_hours)} × ${money(e.rate)}/hr (taxes and fees included)`} value={money(e.total)} testId="estimate-total" strong />
    </div>
  );
}

export default function BallparkEstimator({ legs, category, testId = "ballpark" }) {
  const api = useApi();
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(category || "midsize");
  const [s, setS] = useState(null);
  const [e, setE] = useState(null);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (open) { setCat(category || "midsize"); api.get("/estimator/settings").then((r) => setS(r.data)).catch(() => {}); } }, [open, category, api]);
  useEffect(() => {
    if (!open) return;
    setE(null); setErr("");
    const body = { category: cat, legs: legs.map((l) => ({ from_airport: l.from_airport, to_airport: l.to_airport, pax: Number(l.pax) || 1 })) };
    if (body.legs.some((l) => l.from_airport.length < 2 || l.to_airport.length < 2)) { setErr("Fill in the From and To airports for every leg first."); return; }
    api.post("/estimator/estimate", body).then((r) => setE(r.data)).catch((x) => setErr(errMsg(x)));
  }, [open, cat, legs, s, api]);

  return (
    <>
      <button type="button" className="frame-btn !py-2.5" onClick={() => setOpen(true)} data-testid={`${testId}-btn`}><Calculator size={13} /> Ballpark estimate</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111319] border-white/10 max-w-xl max-h-[92vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <p className="eyebrow">Ballpark estimate</p>
            <DialogTitle className="font-serif-display text-3xl font-normal text-white">Rough price, not a quote</DialogTitle>
          </DialogHeader>
          {editing && s ? <EstimatorSettings s={s} onCancel={() => setEditing(false)} onSaved={(d) => { setS(d); setEditing(false); }} /> : (
            <div className="space-y-5">
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="bg-[#0d0f14] border-white/10" data-testid="estimate-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}{s && !s.rates[c.value] ? " · no rate yet" : ""}</SelectItem>)}</SelectContent>
              </Select>
              {err && <p className="text-sm text-rose-500" data-testid="estimate-error">{err}</p>}
              {!err && !e && <p className="text-sm text-zinc-500">Calculating…</p>}
              {e && (
                <>
                  <div>
                    <p className="font-serif-display text-4xl text-white" data-testid="estimate-range">{money(e.low)} – {money(e.high)}</p>
                    <p className="text-xs text-zinc-400 mt-1">{legs.map((l) => `${code(l.from_airport)}→${code(l.to_airport)}`).join(" · ")} · hourly rates already include taxes and fees</p>
                  </div>
                  <Breakdown e={e} />
                </>
              )}
              <p className="text-[11px] text-zinc-500" data-testid="estimate-disclaimer">For a client who just wants a rough idea. Based on estimated flight time × a typical all-in hourly rate (taxes and fees included), with a {s?.min_hours_per_leg ?? 2}-hour minimum per leg{s?.reposition_hours ? ` and ${s.reposition_hours}h repositioning` : ""}, ±{s?.range_pct ?? 10}%. Real operator quotes can differ (availability, positioning, overnight and landing fees).</p>
              {me?.role === "admin" && s && (
                <button type="button" className="text-[0.62rem] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#5B7FD1] inline-flex items-center gap-1.5" onClick={() => setEditing(true)} data-testid="estimator-edit-settings-btn"><Settings2 size={12} /> Edit rates and rules</button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
